import { useLanguage } from '@vocab/react';
import debounce from 'lodash.debounce';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ROUTE_SUCCESS } from 'src/app/shared/constants/routes';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { log } from 'src/app/utils/logging';
import { actionUpdateProductSetId } from 'src/app/v1/state/modules/tealium/productSetId';
import { isSubCategoryIdValid } from 'src/app/v1/views/components/JobCategory/utils/isSubCategoryValid';
import { UserIntentType } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

export const useShoppingCartForAdPosting = () => {
  const { jobLocationId, jobTitle, subClassificationId, draftId } = useSelector(
    (state: AppState) => ({
      jobLocationId: state.formData.jobLocationId,
      jobTitle: state.formData.jobTitle,
      subClassificationId: state.session.seekSubClassificationId,
      draftId: state.session.draftId,
    }),
    shallowEqual,
  );

  const { pathname } = useLocation();
  const isSuccessPage = pathname.includes(ROUTE_SUCCESS);
  const { locale, language } = useLanguage();
  const dispatch = useDispatch();

  const { setCartId, updateInputData, cartState } = useShoppingCart();
  const debouncedUpdateInputData = useRef(
    debounce((data) => updateInputData(data), 1000),
  ).current;

  useEffect(() => {
    if (isSuccessPage) {
      return;
    }

    if (draftId) {
      try {
        setCartId(draftId);
      } catch (error) {
        log.error('useShoppingCartForAdPosting | setCartId error', {
          error,
        });
      }
    }
  }, [draftId, isSuccessPage, setCartId]);

  useEffect(() => {
    if (isSuccessPage) {
      return;
    }

    const localeForCreateProductSet = locale || language;
    const isCreateProductSetDataReady =
      draftId &&
      jobLocationId &&
      jobTitle &&
      localeForCreateProductSet &&
      isSubCategoryIdValid(Number(subClassificationId));

    if (isCreateProductSetDataReady) {
      try {
        debouncedUpdateInputData({
          draftId,
          locale: localeForCreateProductSet,
          locationId: jobLocationId,
          opportunityId: draftId,
          roleTitle: jobTitle,
          subClassificationId,
          userIntent: UserIntentType.PURCHASE,
        });
      } catch (error) {
        log.error('useShoppingCartForAdPosting | updateInputData error', {
          error,
        });
      }
    }

    return () => {
      debouncedUpdateInputData.cancel();
    };
  }, [
    draftId,
    isSuccessPage,
    jobLocationId,
    jobTitle,
    language,
    locale,
    subClassificationId,
    updateInputData,
    debouncedUpdateInputData,
  ]);

  useEffect(() => {
    if (cartState?.productSetId) {
      dispatch(actionUpdateProductSetId(cartState.productSetId));
    }
  }, [cartState?.productSetId, dispatch]);
};
